import { supabase } from "../supabaseClient";
import { cancelSignupWithRefund } from "./signupPayments";

const bowlerPayload = (bowler) => ({
  name: bowler.name,
  email: bowler.email,
  phone: bowler.phone,
  usbcId: bowler.usbcId,
  average: bowler.average,
});

export const rpcErrorMessage = (error, fallback = "Request failed") => {
  const raw = String(error?.message || "").trim();
  if (!raw) return fallback;
  const stripped = raw.replace(/^.*ERROR:\s*/i, "").split("\n")[0].trim();
  return stripped || fallback;
};

export const invokeCreateSignup = async ({
  email,
  eventID,
  squadID,
  primaryBowler,
  teammates = [],
  selectedSidepots = {},
  averageProofPath = null,
  bracketTickets = {},
  client = supabase,
}) => {
  const { data, error } = await client.rpc("create_secured_signup", {
    p_payload: {
      email,
      eventID,
      squadID,
      primaryBowler: bowlerPayload(primaryBowler),
      teammates: teammates.map(bowlerPayload),
      selectedSidepots,
      averageProofPath,
      bracketTickets,
    },
  });

  if (error) {
    throw new Error(rpcErrorMessage(error, "Signup failed"));
  }
  if (data && data.success === false && data.error) {
    throw new Error(data.error);
  }
  return data;
};

export const fetchMySignups = async ({ client = supabase } = {}) => {
  const { data, error } = await client.rpc("get_my_signups");
  if (error) {
    throw new Error(rpcErrorMessage(error, "Could not load signups"));
  }
  if (data?.error) {
    throw new Error(data.error);
  }
  return data;
};

export const payCartAtDesk = async ({ tournamentId, client = supabase } = {}) => {
  const { data, error } = await client.rpc("pay_cart_at_desk", {
    p_tournament_id: tournamentId,
  });
  if (error) {
    throw new Error(rpcErrorMessage(error, "Could not close Cart."));
  }
  if (data?.error) {
    throw new Error(data.error);
  }
  return data;
};

export const cancelSecuredSignup = async ({
  signupId,
  signupToken,
  paymentStatus,
  cardRefund = false,
  client = supabase,
} = {}) => {
  if (signupId && !signupToken && (paymentStatus === "paid" || cardRefund)) {
    return cancelSignupWithRefund({ signupId });
  }
  const { data, error } = await client.rpc("cancel_secured_signup", {
    p_signup_id: signupId || null,
    p_signup_token: signupToken || null,
  });
  if (error) {
    throw new Error(rpcErrorMessage(error, "Could not cancel this signup"));
  }
  if (data?.error) {
    throw new Error(data.error);
  }
  return data;
};

const directorRpc = async (name, args, fallback) => {
  const { data, error } = await supabase.rpc(name, args);
  if (error) {
    throw new Error(rpcErrorMessage(error, fallback));
  }
  if (data?.error) {
    throw new Error(data.error);
  }
  return data;
};

export const directorSetSignupPayment = async ({
  signupId,
  paymentStatus,
}) =>
  directorRpc(
    "director_set_signup_payment",
    {
      p_signup_id: signupId,
      p_payment_status: paymentStatus,
    },
    "Could not update payment."
  );

export const directorCancelSignup = async ({ signupId }) =>
  directorRpc(
    "director_cancel_signup",
    { p_signup_id: signupId },
    "Could not remove this signup."
  );

export const directorPromoteWaitlist = async ({ signupId }) =>
  directorRpc(
    "director_promote_waitlist",
    { p_signup_id: signupId },
    "Could not put this signup on the squad."
  );

export const directorPromoteNextWaitlist = async ({ squadId }) =>
  directorRpc(
    "director_promote_next_waitlist",
    { p_squad_id: squadId },
    "Could not put the next waitlist signup on the squad."
  );

export const directorMoveSignup = async ({ signupId, squadId }) =>
  directorRpc(
    "director_move_signup",
    {
      p_signup_id: signupId,
      p_squad_id: squadId,
    },
    "Could not move this signup."
  );

export const replaceSignupAverageProof = async ({ signupId, path }) =>
  directorRpc(
    "replace_signup_average_proof",
    {
      p_signup_id: signupId,
      p_path: path,
    },
    "Could not save average verification."
  );

export const getTournamentSignups = async ({ tournamentId }) =>
  directorRpc(
    "get_tournament_signups",
    { p_tournament_id: tournamentId },
    "Could not load signups."
  );

export const getSignupAttentionCount = async ({ tournamentId }) =>
  directorRpc(
    "get_signup_attention_count",
    { p_tournament_id: tournamentId },
    "Could not load signup counts."
  );
